import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useSocketNotifications } from '@/hooks/useSocketNotifications';
import { useStaffMessagingInvalidation } from '@/hooks/useStaffMessagingInvalidation';
import { StaffSocketBridge } from '@/components/layout/StaffSocketBridge';
import { ClientStatusBanner } from './ClientStatusBanner';
import { MaintenanceModeOverlay } from './MaintenanceModeOverlay';
import { cn } from '@/utils/cn';

export default function AppLayout() {
  useDocumentTitle();
  useSocketNotifications();
  useStaffMessagingInvalidation();
  const location = useLocation();
  const isWhatsApp = location.pathname.startsWith('/whatsapp');

  useEffect(() => {
    if (typeof navigator !== 'undefined' && 'storage' in navigator && navigator.storage?.persist) {
      void navigator.storage.persist();
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission === 'default') {
      void Notification.requestPermission();
    }
  }, []);

  /** Un solo polling per manutenzione (fallback se Socket.IO assente); il hook è usato in più componenti. */
  useEffect(() => {
    const POLL_MS = 45_000;
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      window.dispatchEvent(new Event('settings:changed'));
    };
    const id = window.setInterval(tick, POLL_MS);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!isWhatsApp) return;
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    };
  }, [isWhatsApp]);

  return (
    <div className={cn(isWhatsApp ? 'h-dvh overflow-hidden bg-gray-50' : 'min-h-screen bg-gray-50')}>
      <StaffSocketBridge />
      <MaintenanceModeOverlay />
      <Sidebar />
      <div className={cn('lg:pl-64', isWhatsApp && 'flex h-full min-h-0 flex-col')}>
        <Header />
        <ClientStatusBanner />
        <main
          className={cn(
            'min-w-0',
            isWhatsApp
              ? 'flex min-h-0 flex-1 flex-col overflow-hidden p-0'
              : 'py-4 pt-20 lg:pt-4'
          )}
        >
          <div
            className={cn(
              'w-full min-w-0',
              isWhatsApp ? 'flex h-full min-h-0 flex-1 flex-col' : 'px-4 sm:px-5 lg:px-6'
            )}
          >
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
